import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, MoreHorizontal, Pencil, Ban, CheckCircle2, Download, Upload, FileDown, History } from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeID } from "@/lib/format";

const STATUS_STYLE = { ACTIVE: "status-active", INACTIVE: "status-inactive" };

// Field metadata is optional. Some fields render as select if we have a preset.
const PRESETS = {
  category_customer: ["Retail","Supermarket","Modern Market","Distributor","Reseller","Marketplace","Institusi","Perorangan","Lainnya"],
  category_supplier: ["Mitra Manufaktur","Bahan Baku","Kemasan","Logistik","Jasa","Operasional","Marketing","Teknologi","Profesional","Lainnya"],
  payment_term: ["Tunai","COD","7 Hari","14 Hari","30 Hari","45 Hari","60 Hari","90 Hari","Kustom"],
  warehouse_type: ["Gudang Utama","Gudang Transit","Gudang Mitra","Gudang Distributor","Lainnya"],
  pkp_status: ["PKP","Non-PKP"],
  ppn_status: ["Kena PPN","Non-PPN","Tidak Kena PPN"],
  tax_status: ["Kena Pajak","Tidak Kena Pajak"],
  normal_balance: ["Debit","Kredit"],
  category_coa: ["ASET","LIABILITAS","EKUITAS","PENDAPATAN","HPP","BEBAN","PENDAPATAN LAIN","BEBAN LAIN","PAJAK"],
  tax_type: ["PPN","PPh 21","PPh 22","PPh 23","PPh 25","PPh Final","Lainnya"],
  source_kurs: ["Bank Indonesia","Bank","Manual","Sumber Lainnya"],
  project_status: ["Direncanakan","Aktif","Selesai","Ditangguhkan","Dibatalkan"],
  opening_category: ["Kas","Bank","Piutang","Hutang","Akun","Aset"],
  post_status: ["Belum Diposting","Sudah Diposting"],
  doc_type: ["INV","PO","PI","RC","PAY","JV","EXP","AST"],
};

function detectPreset(masterKey, field) {
  const n = field.name;
  if (masterKey === "customers" && n === "category") return PRESETS.category_customer;
  if (masterKey === "suppliers" && n === "category") return PRESETS.category_supplier;
  if (n === "payment_term") return PRESETS.payment_term;
  if (n === "warehouse_type") return PRESETS.warehouse_type;
  if (n === "pkp_status") return PRESETS.pkp_status;
  if (n === "ppn_status") return PRESETS.ppn_status;
  if (n === "tax_status") return PRESETS.tax_status;
  if (n === "normal_balance") return PRESETS.normal_balance;
  if (masterKey === "chart_of_accounts" && n === "category") return PRESETS.category_coa;
  if (n === "tax_type") return PRESETS.tax_type;
  if (n === "source") return PRESETS.source_kurs;
  if (n === "project_status") return PRESETS.project_status;
  if (masterKey === "opening_balances" && n === "category") return PRESETS.opening_category;
  if (n === "post_status") return PRESETS.post_status;
  if (n === "doc_type") return PRESETS.doc_type;
  return null;
}

export default function MasterCrud({ masterKey, primaryColumns, tooltips = {}, formSections = null }) {
  const { user } = useAuth();
  const [spec, setSpec] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyLog, setHistoryLog] = useState([]);
  const fileInput = useRef(null);

  const can = useCallback((act) => permissions.includes(act) || user?.role === "super_admin", [permissions, user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status !== "all") params.status = status;
      const { data } = await api.get(`/masters/${masterKey}`, { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [masterKey, q, status]);

  useEffect(() => {
    Promise.all([
      api.get(`/masters/${masterKey}/spec`),
      api.get(`/masters/permissions`),
    ]).then(([s, p]) => {
      setSpec(s.data);
      setPermissions(p.data.permissions[masterKey] || []);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [masterKey]);

  useEffect(() => { load(); }, [status, load]);

  const openCreate = () => {
    const initial = {};
    spec?.fields.forEach((f) => { initial[f.name] = f.kind === "boolean" ? false : ""; });
    setForm(initial); setEditing(null); setDialogOpen(true);
  };
  const openEdit = (item) => {
    const initial = {};
    spec?.fields.forEach((f) => { initial[f.name] = item[f.name] ?? (f.kind === "boolean" ? false : ""); });
    setForm({ ...initial, _version: item.version });
    setEditing(item); setDialogOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) {
        await api.put(`/masters/${masterKey}/item/${editing.id}`, form);
        toast.success(`${spec.label} berhasil diperbarui.`);
      } else {
        await api.post(`/masters/${masterKey}`, form);
        toast.success(`${spec.label} berhasil ditambahkan.`);
      }
      setDialogOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (item) => {
    try {
      await api.post(`/masters/${masterKey}/item/${item.id}/toggle-status`);
      toast.success(item.status === "ACTIVE" ? `${spec.label} berhasil dinonaktifkan.` : `${spec.label} berhasil diaktifkan.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("abs_token");
      const res = await fetch(`${API}/masters/${masterKey}/export/csv`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Gagal mengekspor.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${masterKey}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Data berhasil diekspor.");
    } catch (e) { toast.error(e.message); }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("abs_token");
      const res = await fetch(`${API}/masters/${masterKey}/template/csv`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("Gagal mengunduh template.");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${masterKey}_template.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const doImportPreview = async () => {
    if (!importFile) return toast.error("Silakan pilih berkas CSV.");
    const fd = new FormData(); fd.append("file", importFile);
    try {
      const { data } = await api.post(`/masters/${masterKey}/import/preview`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      setImportPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doImportConfirm = async () => {
    if (!importFile) return;
    const fd = new FormData(); fd.append("file", importFile);
    try {
      const { data } = await api.post(`/masters/${masterKey}/import/confirm`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(`Impor selesai: ${data.inserted} baris berhasil, ${data.errors.length} gagal.`);
      setImportOpen(false); setImportPreview(null); setImportFile(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openHistory = async (item) => {
    setHistoryItem(item);
    try {
      const { data } = await api.get(`/masters/${masterKey}/item/${item.id}/history`);
      setHistoryLog(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cols = useMemo(() => primaryColumns || (spec?.fields.slice(0, 4).map((f) => f.name) || []), [primaryColumns, spec]);
  const colLabel = (name) => spec?.fields.find((f) => f.name === name)?.label || name;

  const renderCell = (item, name) => {
    const f = spec?.fields.find((x) => x.name === name);
    const v = item[name];
    if (f?.kind === "boolean") return v ? "Ya" : "Tidak";
    if (f?.kind === "number" && v != null && v !== "") {
      if (["credit_limit","opening_balance","default_buy_price","default_sell_price","hpp_reference","amount","budget_reference","buy_rate","sell_rate","mid_rate"].includes(name)) {
        return "Rp " + Number(v).toLocaleString("id-ID");
      }
      return Number(v).toLocaleString("id-ID");
    }
    return v ?? "-";
  };

  if (!spec) return <div className="text-slate-500">Memuat data...</div>;

  const renderFormField = (f) => {
    const preset = detectPreset(masterKey, f);
    const value = form[f.name] ?? (f.kind === "boolean" ? false : "");
    const label = <Label className="text-xs" title={tooltips[f.name] || undefined}>{f.label}{f.required && <span className="text-rose-600"> *</span>}</Label>;
    if (f.kind === "boolean") {
      return (
        <div key={f.name} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2">
          <div>{label}{tooltips[f.name] && <div className="text-[11px] text-slate-500 mt-0.5">{tooltips[f.name]}</div>}</div>
          <Switch checked={!!value} onCheckedChange={(v) => setForm({ ...form, [f.name]: v })} data-testid={`field-${f.name}`} />
        </div>
      );
    }
    if (preset) {
      return (
        <div key={f.name} className="space-y-1.5">
          {label}
          <Select value={value || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
            <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder={`Pilih ${f.label.toLowerCase()}`} /></SelectTrigger>
            <SelectContent>
              {preset.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          {tooltips[f.name] && <div className="text-[11px] text-slate-500">{tooltips[f.name]}</div>}
        </div>
      );
    }
    if (f.name === "notes" || f.name === "description" || f.name === "address") {
      return (
        <div key={f.name} className="space-y-1.5 sm:col-span-2">
          {label}
          <Textarea rows={2} value={value} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
        </div>
      );
    }
    const inputType = (f.name === "start_date" || f.name === "end_date" || f.name === "effective_date" || f.name === "effective_from" || f.name === "effective_to" || f.name === "opening_balance_date" || f.name === "date") ? "date"
      : f.kind === "number" ? "number" : (f.name === "email" ? "email" : "text");
    return (
      <div key={f.name} className="space-y-1.5">
        {label}
        <Input type={inputType} value={value} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
        {tooltips[f.name] && <div className="text-[11px] text-slate-500">{tooltips[f.name]}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid={`master-${masterKey}-page`}>
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder={`Cari ${spec.label.toLowerCase()}...`} value={q}
                     onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
                     data-testid={`master-${masterKey}-search`} />
            </div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid={`master-${masterKey}-status-filter`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="ACTIVE">Aktif</SelectItem>
                <SelectItem value="INACTIVE">Tidak Aktif</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2 flex-wrap justify-end">
              <Button variant="outline" onClick={load} data-testid={`master-${masterKey}-refresh`}>Muat Ulang</Button>
              {can("EXPORT") && (
                <Button variant="outline" onClick={exportCsv} data-testid={`master-${masterKey}-export`}>
                  <Download className="h-4 w-4 mr-1.5" /> Ekspor
                </Button>
              )}
              {can("IMPORT") && (
                <Button variant="outline" onClick={() => setImportOpen(true)} data-testid={`master-${masterKey}-import`}>
                  <Upload className="h-4 w-4 mr-1.5" /> Impor
                </Button>
              )}
              {can("CREATE") && (
                <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`master-${masterKey}-add`}>
                  <Plus className="h-4 w-4 mr-1.5" /> Tambah
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {cols.map((c) => <TableHead key={c}>{colLabel(c)}</TableHead>)}
                  <TableHead>Status</TableHead>
                  <TableHead>Terakhir Diubah</TableHead>
                  <TableHead className="text-right">Tindakan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={cols.length + 3} className="text-center py-10 text-slate-500">Memuat data...</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={cols.length + 3} className="text-center py-10 text-slate-500">Belum ada data.</TableCell></TableRow>
                ) : items.map((item) => (
                  <TableRow key={item.id}>
                    {cols.map((c) => <TableCell key={c}>{renderCell(item, c)}</TableCell>)}
                    <TableCell><span className={`status-pill ${STATUS_STYLE[item.status] || "status-inactive"}`}>{item.status === "ACTIVE" ? "Aktif" : "Tidak Aktif"}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDateTimeID(item.updated_at || item.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`row-menu-${item.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          {can("EDIT") && <DropdownMenuItem onClick={() => openEdit(item)}><Pencil className="h-4 w-4 mr-2" /> Ubah</DropdownMenuItem>}
                          {can("VIEW") && <DropdownMenuItem onClick={() => openHistory(item)}><History className="h-4 w-4 mr-2" /> Riwayat Perubahan</DropdownMenuItem>}
                          {can("DEACTIVATE") && (
                            <DropdownMenuItem
                              onClick={() => setConfirm({ item, action: item.status === "ACTIVE" ? "deactivate" : "activate" })}>
                              {item.status === "ACTIVE" ? <><Ban className="h-4 w-4 mr-2" /> Nonaktifkan</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Aktifkan</>}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-xs text-slate-500">Total Data: <span className="font-semibold text-slate-700">{items.length}</span></div>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Ubah ${spec.label}` : `Tambah ${spec.label}`}</DialogTitle>
            <DialogDescription>Lengkapi informasi berikut ini. Kolom bertanda * wajib diisi.</DialogDescription>
          </DialogHeader>
          {formSections ? (
            <div className="space-y-6">
              {formSections.map((sec) => (
                <div key={sec.title}>
                  <div className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3 pb-1 border-b border-slate-200">{sec.title}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {sec.fields.map((n) => {
                      const f = spec.fields.find((x) => x.name === n);
                      if (!f) return null;
                      return renderFormField(f);
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {spec.fields.map((f) => renderFormField(f))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid={`master-${masterKey}-submit`}>
              {editing ? "Simpan Perubahan" : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.action === "deactivate" ? `Nonaktifkan ${spec.label}?` : `Aktifkan ${spec.label}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "deactivate"
                ? `Apakah Anda yakin ingin menonaktifkan ${spec.label.toLowerCase()} ini? Data tetap tersimpan namun tidak akan tampil sebagai data aktif.`
                : `Apakah Anda yakin ingin mengaktifkan kembali ${spec.label.toLowerCase()} ini?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={async () => { await toggleStatus(confirm.item); setConfirm(null); }}>
              Ya, Lanjutkan
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportPreview(null); setImportFile(null); }}}>
        <DialogContent className="bg-white max-w-3xl">
          <DialogHeader>
            <DialogTitle>Impor {spec.label}</DialogTitle>
            <DialogDescription>Unggah berkas CSV. Sistem akan memvalidasi terlebih dahulu sebelum menyimpan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={downloadTemplate}><FileDown className="h-4 w-4 mr-1.5" /> Unduh Template</Button>
              <Input ref={fileInput} type="file" accept=".csv" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="max-w-xs" />
              <Button variant="outline" onClick={doImportPreview} disabled={!importFile}>Pratinjau</Button>
            </div>
            {importPreview && (
              <div className="text-sm space-y-2">
                <div>Total Baris: <b>{importPreview.total}</b> · Valid: <b className="text-emerald-700">{importPreview.valid}</b> · Tidak Valid: <b className="text-rose-700">{importPreview.invalid}</b></div>
                {importPreview.errors.length > 0 && (
                  <div className="border border-rose-200 bg-rose-50 rounded-md p-3 max-h-52 overflow-auto">
                    <div className="text-xs font-semibold text-rose-800 mb-1">Kesalahan:</div>
                    <ul className="text-xs space-y-1">
                      {importPreview.errors.map((e, i) => (
                        <li key={i}>Baris {e.row} · {e.message} <span className="text-slate-500">— {e.suggestion}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {importPreview.valid > 0 && (
                  <Button onClick={doImportConfirm} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    Konfirmasi Impor ({importPreview.valid} baris)
                  </Button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyItem} onOpenChange={(v) => !v && setHistoryItem(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Riwayat Perubahan</DialogTitle>
            <DialogDescription>{historyItem?.name || historyItem?.[spec?.code_field]}</DialogDescription>
          </DialogHeader>
          {historyLog.length === 0 ? (
            <div className="text-sm text-slate-500 py-4 text-center">Belum ada riwayat perubahan.</div>
          ) : (
            <div className="space-y-3">
              {historyLog.map((h) => (
                <div key={h.id} className="border border-slate-200 rounded-md p-3">
                  <div className="text-xs text-slate-500 flex items-center justify-between mb-2">
                    <span>{h.user_name}</span>
                    <span>{formatDateTimeID(h.timestamp)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="font-semibold text-slate-500 mb-1">Sebelum</div>
                      <pre className="bg-slate-50 border border-slate-200 rounded p-2 max-h-40 overflow-auto">{JSON.stringify(h.before, null, 2)}</pre>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-500 mb-1">Sesudah</div>
                      <pre className="bg-emerald-50 border border-emerald-200 rounded p-2 max-h-40 overflow-auto">{JSON.stringify(h.after, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
